import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "@phosphor-icons/react";
import { type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { dropdownContentCollisionClassName, floatingCollisionPadding, floatingLayerClassName } from "../overlayPositioning";

export type DropdownItem = {
  label: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  checked?: boolean;
};

export type DropdownProps = {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: "start" | "center" | "end";
  size?: "sm" | "md" | "lg";
  className?: string;
};

export function Dropdown({ trigger, items, align = "end", size = "md", className }: DropdownProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <BfrsPortalScope>
          <DropdownMenu.Content
            align={align}
            sideOffset={6}
            collisionPadding={floatingCollisionPadding}
            className={cn(floatingLayerClassName, "bfrs-min-w-48 bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-elevated bfrs-animate-content-in", dropdownContentCollisionClassName, className)}
          >
            {items.map((item, index) => (
              <DropdownMenu.Item
                key={index}
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  "bfrs-flex bfrs-cursor-default bfrs-select-none bfrs-items-center bfrs-gap-2 bfrs-rounded-sm bfrs-text-text-secondary bfrs-outline-none data-[disabled]:bfrs-pointer-events-none data-[highlighted]:bfrs-bg-primary-soft data-[disabled]:bfrs-text-text-disabled",
                  size === "sm" && "bfrs-px-3 bfrs-py-2 bfrs-text-xs",
                  size === "md" && "bfrs-px-3 bfrs-py-2 bfrs-text-sm",
                  size === "lg" && "bfrs-px-4 bfrs-py-3 bfrs-text-sm",
                  item.destructive && "bfrs-text-danger"
                )}
              >
                <span className="bfrs-size-4">{item.checked && <Check size={14} weight="bold" />}</span>
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </BfrsPortalScope>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
